import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

export const reviewIdFromPath = pathname => {
  const match = String(pathname || "").match(/^\/reviews\/([^/?#]+)\/?$/i);
  if (!match) return null;
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
};

const scoreLabel = (value, max) => value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? "—" : `${Number(value)}/${max}`;

export default function ReviewDeepLinkPage({ reviewId }) {
  const [review, setReview] = useState(null);
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data: reviewRow, error: reviewError } = await supabase
          .from("reviews")
          .select("id,placeid,userid,username,sabor,precio,servicio,satisfaccion,platillo,comentario,precio_label,fecha,imagenes,pending,moderation_status,metadata,total_bill,party_size,cost_per_person")
          .eq("id", reviewId)
          .maybeSingle();
        if (reviewError) throw reviewError;
        if (!reviewRow) throw new Error("No encontramos esta reseña o todavía no está disponible.");

        let placeRow = null;
        if (reviewRow.placeid != null) {
          const { data, error: placeError } = await supabase
            .from("restaurants")
            .select("id,name,tipo,location,image,maps")
            .eq("id", reviewRow.placeid)
            .maybeSingle();
          if (placeError) throw placeError;
          placeRow = data || null;
        }

        if (!active) return;
        setReview(reviewRow);
        setPlace(placeRow);
      } catch (err) {
        if (active) setError(err?.message || "No se pudo abrir la reseña.");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [reviewId]);

  const ratings = useMemo(() => review ? [
    ["Sabor", review.sabor, 5],
    ["Precio", review.precio, 7],
    ["Servicio", review.servicio, 5],
    ["Satisfacción", review.satisfaccion, 5],
  ] : [], [review]);

  const statusLabel = review?.moderation_status === "rejected" || review?.metadata?.rejected
    ? "Reseña necesita cambios"
    : review?.pending || review?.moderation_status === "pending"
      ? "Reseña en revisión"
      : "Reseña aprobada";
  const statusColor = statusLabel === "Reseña aprobada" ? "#81c784" : statusLabel === "Reseña en revisión" ? "#f6b94a" : "#ff8a80";

  return (
    <main style={{ minHeight: "100vh", background: "#070707", color: "#f5f5f5", padding: "24px 16px 48px", boxSizing: "border-box", fontFamily: "Arial, sans-serif" }}>
      <div style={{ width: "min(680px, 100%)", margin: "0 auto" }}>
        <button type="button" onClick={() => window.location.assign("/")} style={{ border: "1px solid #333", background: "#151515", color: "#ddd", borderRadius: 10, padding: "9px 12px", cursor: "pointer", fontWeight: 700 }}>← Venaqqi</button>

        <section aria-label="Detalle de reseña" style={{ marginTop: 18, background: "#111", border: "1px solid #292929", borderRadius: 18, overflow: "hidden", boxShadow: "0 18px 55px rgba(0,0,0,.38)" }}>
          <div style={{ height: 4, background: "#e53935" }} />
          <div style={{ padding: "22px 20px" }}>
            {loading && <p style={{ color: "#aaa", margin: 0 }}>Abriendo reseña…</p>}
            {!loading && error && <div role="alert"><h1 style={{ margin: "0 0 8px", fontSize: 20 }}>No pudimos abrirla</h1><p style={{ color: "#aaa", lineHeight: 1.55 }}>{error}</p></div>}
            {!loading && review && <>
              <div style={{ color: statusColor, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".08em" }}>{statusLabel}</div>
              <h1 style={{ margin: "6px 0 3px", fontSize: 24, lineHeight: 1.2 }}>{place?.name || "Reseña Venaqqi"}</h1>
              {place?.location && <p style={{ margin: "0 0 18px", color: "#777", fontSize: 12 }}>{place.location}</p>}

              {review.platillo && <div style={{ marginBottom: 14 }}><span style={{ color: "#777", fontSize: 11 }}>Platillo</span><div style={{ fontSize: 17, fontWeight: 800 }}>{review.platillo}</div></div>}
              {review.comentario && <blockquote style={{ margin: "0 0 18px", padding: "14px 15px", background: "#171717", borderLeft: "3px solid #e53935", borderRadius: 10, color: "#ddd", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{review.comentario}</blockquote>}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginBottom: 16 }}>
                {ratings.map(([label, value, max]) => <div key={label} style={{ background: "#161616", borderRadius: 10, padding: 10 }}><div style={{ color: "#777", fontSize: 10 }}>{label}</div><strong>{scoreLabel(value, max)}</strong></div>)}
              </div>

              {(review.cost_per_person != null || review.precio_label) && <p style={{ margin: "0 0 8px", color: "#bbb", fontSize: 12 }}><strong>Costo:</strong> {review.cost_per_person != null ? `$${Number(review.cost_per_person).toFixed(0)} por persona` : review.precio_label}</p>}
              {review.fecha && <p style={{ margin: 0, color: "#666", fontSize: 11 }}>{review.fecha}</p>}
            </>}
          </div>
        </section>
      </div>
    </main>
  );
}
